import { updateVariableValue } from 'components/editor/grid/reduxStore/allVariableSlice';
import SidePanelAutoComplete from '../../component/SidePanelAutoComplete';
import { useCallback, useMemo, useState } from 'react';
import { debounce } from 'utils/debounce';
import type { EditableFieldSharedProps } from './types';
import MenuItem from '@mui/material/MenuItem';

export function CustomFieldsAutoComplete({ field, mutate, dispatch, currentValue, fieldTypeOption, t }: EditableFieldSharedProps) {
  const [options, setOptions] = useState<string[]>(field.options || []);

  const debouncedMutate = useMemo(() => debounce(mutate, 500), [mutate]);

  const handleChange = useCallback(
    (newValue: string | null | undefined) => {
      if (!newValue) return;

      if (newValue && newValue.length > 100) return;

      dispatch(updateVariableValue({ variableName: field.name, newValue }));
      debouncedMutate({ id: field.id, type: field.type, value: newValue === '' ? null : newValue });
    },
    [dispatch, field, debouncedMutate]
  );

  return (
    <SidePanelAutoComplete<string>
      sx={{ mb: 0 }}
      label={field.name}
      options={options}
      onInputChange={(searchValue: string | null) => {
        const sourceOptions = field.options || [];
        if (!searchValue) {
          setOptions(sourceOptions);
          return;
        }
        const filtered = sourceOptions.filter((option) => option.toLowerCase().includes(searchValue.toLowerCase()));
        setOptions(filtered);
      }}
      onClear={() => {
        setOptions(field.options || []);
      }}
      getOptionLabel={(option: string) => option}
      noOptionsText={t('editor.variables.custom_fields_options_type_no_options', 'No Options')}
      value={currentValue ? String(currentValue) : null}
      testId={`${field.name}-select`}
      textDataTestId={`${field.name}-select-text-input`}
      placeholder={field.placeholder || t(fieldTypeOption.defaultPlaceholderKey)}
      isOptionEqualToValue={(option, value) => option === value}
      onChange={(newValue) => handleChange(newValue)}
      renderOptions={(props, options: string) => (
        <MenuItem {...props} key={options} value={options}>
          {options}
        </MenuItem>
      )}
    />
  );
}
